/*
  cart-drawer-stars.js
  WHAT: Injects product review stars next to each line item in the cart
        drawer. Impulse renders line items via AJAX (the [data-products]
        container is populated client-side from /cart.js), so this script
        uses a MutationObserver to inject stars whenever items appear or
        change.
  WHY:  Per user 2026-05-29 "can we have review stars show up in mini tray?"
        Reinforces social proof at the conversion moment.
  WHEN: Added 2026-05-29 with the cart-drawer enhancements pass.

  Strategy:
  - On cart drawer open / cart-update events, scan all line items.
  - For each item, derive the product handle (from data attribute or the
    item's anchor href).
  - Fetch /products/{handle}.json (cached aggressively by Shopify CDN).
  - Read `metafields.reviews.rating` if present.
  - Inject a small star row next to the product title.
  - Mark items as `data-ba-stars="done"` to avoid re-fetching.

  Failure modes are silent — missing handle, missing metafield, fetch
  failure → no stars rendered, line item still displays correctly.
*/

(function () {
  'use strict';

  var drawer = document.querySelector('#CartDrawer');
  if (!drawer) return;

  var itemsContainer = drawer.querySelector('[data-products]');
  if (!itemsContainer) return;

  // Cache product → rating lookups across the session.
  var ratingCache = {};

  function extractHandle(itemEl) {
    if (itemEl.dataset.handle) return itemEl.dataset.handle;
    var link = itemEl.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var m = link.getAttribute('href').match(/\/products\/([a-z0-9-_]+)/i);
    return m ? m[1] : null;
  }

  function fetchRating(handle) {
    if (ratingCache.hasOwnProperty(handle)) {
      return Promise.resolve(ratingCache[handle]);
    }
    return fetch('/products/' + handle + '.json')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data.product) {
          ratingCache[handle] = null;
          return null;
        }
        // Try a few common rating-metafield shapes. Most Shopify shops with
        // Yotpo expose `metafields.reviews.rating` as a number or stringified
        // JSON ({ "value": "4.8" }).
        var rating = null;
        var mf = data.product.metafields || data.product.metafield;
        // Inline metafields from products.json don't always include private/
        // app metafields; if Yotpo's preloaded_bottomline is present, parse it.
        if (mf) {
          if (mf.reviews && mf.reviews.rating) {
            rating = parseFloat(mf.reviews.rating);
          } else if (mf.yotpo && mf.yotpo.preloaded_bottomline) {
            try {
              var p = JSON.parse(mf.yotpo.preloaded_bottomline);
              if (p && p.average_score) rating = parseFloat(p.average_score);
            } catch (e) { /* noop */ }
          }
        }
        // Some shops put it at the top-level (rare): data.product.rating
        if (rating === null && data.product.rating) rating = parseFloat(data.product.rating);
        ratingCache[handle] = isNaN(rating) ? null : rating;
        return ratingCache[handle];
      })
      .catch(function () {
        ratingCache[handle] = null;
        return null;
      });
  }

  function renderStars(rating) {
    var rounded = Math.round(rating * 2) / 2;
    var html = '';
    for (var i = 1; i <= 5; i++) {
      var threshold = i - 0.5;
      if (rounded >= threshold) {
        html += '<span class="ba-cart-stars__s is-filled">★</span>';
      } else {
        html += '<span class="ba-cart-stars__s">☆</span>';
      }
    }
    return (
      '<span class="ba-cart-stars" data-ba-stars-mark>' +
      '<span class="ba-cart-stars__row" aria-label="Rated ' + rating.toFixed(1) + ' out of 5">' + html + '</span>' +
      '<span class="ba-cart-stars__num">' + rating.toFixed(1) + '</span>' +
      '</span>'
    );
  }

  function injectIntoItem(itemEl) {
    if (itemEl.dataset.baStars === 'done' || itemEl.dataset.baStars === 'pending') return;
    var handle = extractHandle(itemEl);
    if (!handle) {
      itemEl.dataset.baStars = 'skip';
      return;
    }
    itemEl.dataset.baStars = 'pending';
    fetchRating(handle).then(function (rating) {
      itemEl.dataset.baStars = 'done';
      if (rating === null || rating === undefined || rating <= 0) return;
      // Try common selectors Impulse uses for the line-item title row.
      var titleEl = itemEl.querySelector(
        '.ajaxcart__product-name, .cart__item-title, [data-cart-title], .grid-product__title, h2, h3, .product-details__name'
      );
      if (!titleEl) titleEl = itemEl.querySelector('a[href*="/products/"]');
      if (!titleEl) return;
      // Avoid double-injection (idempotent).
      if (itemEl.querySelector('[data-ba-stars-mark]')) return;
      var wrap = document.createElement('span');
      wrap.innerHTML = renderStars(rating);
      titleEl.parentNode.insertBefore(wrap.firstChild, titleEl.nextSibling);
    });
  }

  function scanAndInject() {
    // Impulse cart line items use varied class names across themes. Try the
    // common ones; fall back to any direct child of [data-products].
    var items = itemsContainer.querySelectorAll(
      '.ajaxcart__product, .cart__item, [data-cart-item], [data-line-item], .cart-drawer__item'
    );
    if (items.length === 0) {
      items = itemsContainer.children;
    }
    Array.prototype.forEach.call(items, injectIntoItem);
  }

  // Initial scan in case items are already present at page load (e.g. cart
  // has items from a prior session).
  scanAndInject();

  // Watch the items container for any DOM change (item add/remove/edit).
  var obs = new MutationObserver(scanAndInject);
  obs.observe(itemsContainer, { childList: true, subtree: true });

  // Also re-scan on common Shopify cart events.
  document.addEventListener('cart:updated', scanAndInject);
  document.addEventListener('cart:refresh', scanAndInject);
  document.addEventListener('ajaxProduct:added', scanAndInject);
})();
